import React, { useState, useEffect } from 'react';
import { Tag, Plus, CheckCircle2, Clock, Trash2 } from 'lucide-react';
import { apiFetch } from '../lib/auth';

interface DiscountCode {
  id: string;
  code: string;
  type: 'percentage' | 'fixed' | 'shipping';
  value: number;
  usageCount: number;
  usageLimit?: number;
  isActive: boolean;
  expiresAt: string;
}

const INITIAL_DISCOUNTS: DiscountCode[] = [];

export default function DiscountsPage() {
  const [discounts, setDiscounts] = useState<DiscountCode[]>(INITIAL_DISCOUNTS);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [code, setCode] = useState('');
  const [percent, setPercent] = useState('10');

  useEffect(() => {
    async function loadDiscounts() {
      try {
        setLoading(true);
        const res = await apiFetch<{ data: any[] }>('/discounts');
        if (Array.isArray(res.data)) {
          setDiscounts(res.data.map((d: any) => ({
            id: d.id,
            code: d.code,
            type: d.type || 'percentage',
            value: d.value ?? 10,
            usageCount: d.usage_count || d.usageCount || 0,
            usageLimit: d.usage_limit || d.usageLimit,
            isActive: Boolean(d.is_active ?? d.isActive ?? true),
            expiresAt: d.expires_at || d.expiresAt || 'Ongoing',
          })));
        }
      } catch (err) {
        console.error('Failed to load discounts:', err);
      } finally {
        setLoading(false);
      }
    }
    loadDiscounts();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) return;
    const cleanCode = code.trim().toUpperCase();
    const newDisc: DiscountCode = {
      id: `disc_${Date.now()}`,
      code: cleanCode,
      type: 'percentage',
      value: Number(percent) || 10,
      usageCount: 0,
      isActive: true,
      expiresAt: 'Ongoing',
    };
    setDiscounts([newDisc, ...discounts]);
    setCode('');
    setShowModal(false);

    try {
      await apiFetch('/discounts', {
        method: 'POST',
        body: JSON.stringify({
          code: cleanCode,
          type: 'percentage',
          value: Number(percent) || 10,
        }),
      });
    } catch (err) {
      console.error('Failed to persist discount code:', err);
    }
  };

  const toggleStatus = (id: string) => {
    setDiscounts(discounts.map((d) => (d.id === id ? { ...d, isActive: !d.isActive } : d)));
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Promotions & Coupons</h1>
          <p style={styles.subtitle}>Configure promotional codes, percentage discounts, and campaigns</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowModal(true)}>
          <Plus size={16} />
          <span>New Discount Code</span>
        </button>
      </div>

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Promotional Code</th>
              <th style={styles.th}>Discount Value</th>
              <th style={styles.th}>Usage</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Valid Until</th>
              <th style={styles.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {discounts.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '48px 24px', textAlign: 'center', color: '#8A8A8E' }}>
                  {loading ? 'Loading promotional codes…' : 'No promotional codes found. Create a new discount code above to launch a promotion.'}
                </td>
              </tr>
            ) : (
              discounts.map((d) => (
                <tr key={d.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.codeWrap}>
                      <Tag size={15} style={{ color: '#C9A84C' }} />
                      <code style={styles.codeBadge}>{d.code}</code>
                    </div>
                  </td>
                  <td style={styles.td}>{d.value}% Off Entire Order</td>
                  <td style={styles.td}>
                    {d.usageCount} {d.usageLimit ? `/ ${d.usageLimit}` : 'times'}
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => toggleStatus(d.id)}
                      style={d.isActive ? styles.statusActiveBtn : styles.statusPausedBtn}
                    >
                      {d.isActive ? 'Active' : 'Paused'}
                    </button>
                  </td>
                  <td style={styles.tdMuted}>{d.expiresAt}</td>
                  <td style={styles.tdRight}>
                    <button
                      onClick={() => setDiscounts(discounts.filter((x) => x.id !== d.id))}
                      style={styles.iconBtn}
                      title="Delete code"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Create Promotional Code</h2>
            <form onSubmit={handleCreate} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Coupon Code (Uppercase) *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. WELCOME10"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Percentage Discount (%)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  required
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => setShowModal(false)} style={styles.btnSecondary}>
                  Cancel
                </button>
                <button type="submit" style={styles.btnPrimary}>
                  Save Code
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' },
  title: { fontSize: '1.75rem', fontWeight: 600, color: '#FAF7F2', margin: 0 },
  subtitle: { fontSize: '0.875rem', color: '#9B9B9B', margin: '0.25rem 0 0 0' },
  btnPrimary: {
    background: '#C9A84C',
    color: '#1A1A1A',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  btnSecondary: {
    background: 'transparent',
    color: '#FAF7F2',
    border: '1px solid #3A3A3A',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  tableCard: {
    background: '#1F1F1F',
    border: '1px solid #2C2C2C',
    borderRadius: '8px',
    overflow: 'hidden',
    overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' },
  th: { padding: '0.875rem 1.25rem', background: '#161616', color: '#9B9B9B', fontWeight: 500, borderBottom: '1px solid #2C2C2C' },
  thRight: { padding: '0.875rem 1.25rem', background: '#161616', color: '#9B9B9B', fontWeight: 500, textAlign: 'right', borderBottom: '1px solid #2C2C2C' },
  tr: { borderBottom: '1px solid #2A2A2A' },
  td: { padding: '1rem 1.25rem', color: '#FAF7F2' },
  tdMuted: { padding: '1rem 1.25rem', color: '#6B6B6B' },
  tdRight: { padding: '1rem 1.25rem', textAlign: 'right' },
  codeWrap: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  codeBadge: {
    background: '#2B261A',
    color: '#F4D068',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  statusActiveBtn: {
    background: '#163326',
    color: '#4ADE80',
    border: 'none',
    padding: '0.25rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  statusPausedBtn: {
    background: '#2E2E2E',
    color: '#9B9B9B',
    border: 'none',
    padding: '0.25rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  iconBtn: { background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: '0.35rem' },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modal: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '95vw',
    maxWidth: '420px',
  },
  modalTitle: { margin: '0 0 1.25rem 0', color: '#FAF7F2', fontSize: '1.25rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.75rem', color: '#9B9B9B', fontWeight: 500 },
  input: {
    background: '#141414',
    border: '1px solid #333',
    borderRadius: '4px',
    padding: '0.6rem 0.75rem',
    color: '#FAF7F2',
    fontSize: '0.875rem',
  },
  modalButtons: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.75rem' },
};
